import React, { useEffect, useMemo } from 'react';
import Styles from './FormProposal.css';
import clsx from 'clsx';
import { useSimuladorEnergiaSolarState } from '../../model/state';
import { BASE_PRELOADED_INSTALLMENTS, SIMULATION_STEPS } from '../../model/simulation-steps';
import ContainerHome24 from '../../../../components/atoms/ContainerHome24';
import Button from '../../widgets/Button/Button';
import { useScroll } from '../../../../utils/useLayout';
import TextField, { MoneyTextFieldEmptyValue } from '../../widgets/TextField/TextField';
import { Controller } from 'react-hook-form';
import { combineFieldProps } from '../../shared/utils/form';
import { useFormProposal } from '../../model/form-proposal';
import Select from '../../widgets/Select/Select';
import Checkbox from '../../widgets/Checkbox/Checkbox';
import ModalRemoveInsurance from '../ModalRemoveInsurance/ModalRemoveInsurance';
import ModalInstallments from '../ModalInstallments/ModalInstallments';
import ModalVoucher from '../ModalVoucher/ModalVoucher';
import ModalInsuranceDetails from '../ModalInsuranceDetails/ModalInsuranceDetails';
import { devLog } from '../../../../utils/browser';
import { DatePickerInline } from '../../widgets/DatePicker';
import { DateTime } from 'luxon';

function FormProposal() {
  const { scrollToTop, dynamicScrollTo } = useScroll();
  const {
    simulationStep,
    setSimulationStep,
    setModalRemoveInsurance,
    exclusiveModal,
    setModalInstallments,
    updateInsurance,
    setModalVoucher,
    isMobile,
    setModalInsuranceDetails,
    loading,
    simulationType,
    softRestartSimulation,
  } = useSimuladorEnergiaSolarState();

  const hidden = simulationStep?.id !== SIMULATION_STEPS.FORM_PROPOSAL.id;
  const formProposal = useFormProposal();
  const offer = formProposal.offer;

  const { selectedInsurance, offerInsurance } = useMemo(() => {
    const ins = (offer?.insuranceOptions || []).find(
      (o) => o.value === formProposal.currentInsurance,
    );

    const offerIns = offer?.insuranceOptions?.find((o) => o.selected);

    return { selectedInsurance: ins, offerInsurance: offerIns?.value || '' };
  }, [formProposal.currentInsurance, offer?.insuranceOptions]);

  const installments = useMemo(() => {
    const opts = offer?.installmentOptions || [];
    const list = opts.filter((o) => o.loaded);

    return {
      list,
      hasMore: opts.length > list.length,
    };
  }, [offer?.installmentOptions]);

  const hideInsurance = !offer?.insuranceOptions || offer?.insuranceOptions?.length === 0;

  const handleAddInsurance = async (id) => {
    formProposal.form.setValue('insurance', id, true);

    if (id !== offerInsurance) {
      await updateInsurance(true);
    }
  };

  useEffect(() => {
    if (hidden || !offer || loading || !formProposal) return;

    let entry = `${offer.entryValueRecommended || 0}`.replace('R$', '').trim();
    if (entry === '0' || entry === '0,00') {
      entry = MoneyTextFieldEmptyValue;
    }

    formProposal.form.setValue('entryValue', entry);
    formProposal.form.setValue(
      'insurance',
      offer?.insuranceOptions?.find((o) => o.selected)?.value || '',
    );

    if (
      formProposal.currentInstallmentQuantity === 0 &&
      formProposal.currentInstallmentQuantity !== offer?.installmentQuantity &&
      !!offer?.installmentQuantity
    ) {
      formProposal.form.setValue('installmentQuantity', offer?.installmentQuantity || 0);
    }

    formProposal.form.setValue('firstDueDate', offer?.firstDueDate || '');
    formProposal.form.setValue('paymentMethod', offer?.paymentMethod || '');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden, offer, loading, formProposal.currentInstallmentQuantity]);

  return (
    <>
      <div
        id="form-proposal"
        className={clsx({
          proposal: true,
          hidden,
          'exclusive-modal': exclusiveModal,
          'hide-resume': offer?.shouldHideResume,
        })}
      >
        <ContainerHome24>
          <h2 className="info-title">{offer?.greetingText}</h2>

          <div className="info-boxes">
            <div className="box-project">
              <img
                className="box-icon"
                src="assets/simulador-energia-solar/jornada/ic-proposal-value.svg"
                alt=""
              />
              <b className="mt box-title">Valor do projeto</b>
              <p className="box-text">{offer?.projectValue}</p>

              <b className="mt box-title">Economia Anual</b>
              <p className="box-text">{offer?.economyUsageValue}</p>
              <p className="box-text">
                Estimativa de economia pós instalação, com média no seu gasto mensal de{' '}
                {offer?.averageUsageValue}
              </p>
            </div>

            <div className="box-project">
              <img
                className="box-icon"
                src="assets/simulador-energia-solar/jornada/ic-proposal-offer.svg"
                alt=""
              />
              <b className="mt box-title">Garantimos o melhor para você!</b>

              <div className="offer-list">
                <div className="offer-row">
                  <img
                    className="offer-icon"
                    alt=""
                    src="assets/simulador-energia-solar/jornada/ic-offer-energy.svg"
                  />
                  <p className="box-text">
                    {offer?.estimateKitPotency} estimado de potência do kit instalado.
                  </p>
                </div>
                <div className="offer-row">
                  <img
                    className="offer-icon"
                    alt=""
                    src="assets/simulador-energia-solar/jornada/ic-offer-time.svg"
                  />
                  <p className="box-text">
                    Estimativa de retorno do valor investido após {offer?.roiPeriod}
                  </p>
                </div>
                <div className="offer-row">
                  <img
                    className="offer-icon"
                    alt=""
                    src="assets/simulador-energia-solar/jornada/ic-offer-value.svg"
                  />
                  <p className="box-text">
                    Economia de aproximadamente <b>{offer?.estimateLongTermEconomy}</b> na conta de
                    luz ao longo de {offer?.estimateLongTermPeriod}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <p className="text-disclaimer">*Valores estimados de acordo com a média do mercado.</p>

          <p className="text-subtitle">Informe o valor de entrada desejada</p>

          <div className="inputs-wrapper">
            <Controller
              name="entryValue"
              control={formProposal.form.control}
              render={(renderOptions) => {
                const field = formProposal.fields.fieldDefinition.entryValue;
                const fieldProps = combineFieldProps(field, renderOptions);

                return (
                  <div className="field-wrapper">
                    <TextField
                      {...fieldProps}
                      onBlur={(ev) => {
                        if (fieldProps.onBlur) fieldProps.onBlur(ev);
                        formProposal.updateEntryValue(ev.target.value);
                      }}
                    />
                  </div>
                );
              }}
            />

            <div className="field-wrapper">
              <TextField label="Valor do Projeto" disabled value={offer?.projectValue} />
            </div>
          </div>

          <p className="text-subtitle text-choose-installment">Escolha a parcela desejada</p>

          <Button
            variant="flat"
            className="btn-insert-voucher custom-clear-button"
            onClick={() => {
              setModalVoucher(true);

              if (!isMobile) return;
              scrollToTop();
            }}
          >
            <img alt="" src="assets/simulador-energia-solar/jornada/ic-voucher.svg" />
            Inserir cupom
            <img
              className="voucher-arrow"
              alt=""
              src="assets/simulador-energia-solar/jornada/ic-voucher-arrow.svg"
            />
          </Button>

          <Controller
            name="installmentQuantity"
            control={formProposal.form.control}
            render={(renderOptions) => {
              const field = formProposal.fields.fieldDefinition.installmentQuantity;
              const fieldProps = combineFieldProps(field, renderOptions);

              if (renderOptions.field.name === 'installmentQuantity') {
                fieldProps.options = installments.list;
              }

              return (
                <div className="field-wrapper select-installment">
                  <Select
                    {...fieldProps}
                    handleValueChange={(v) => {
                      formProposal.form.setValue('installmentQuantity', v, true);
                    }}
                  />
                </div>
              );
            }}
          />

          <div className="hide-mobile installment-group">
            {installments.list.map((opt) => {
              const [amount, value] = opt.label.split('de');
              const disabled = !opt.loaded;

              const selected = formProposal.currentInstallmentQuantity === opt.value;

              return (
                <div
                  key={opt.quantity}
                  className={clsx({
                    'installment-radio': true,
                    selected,
                    disabled,
                  })}
                  onClick={(ev) => {
                    ev.preventDefault();
                    if (disabled) return;

                    formProposal.form.setValue('installmentQuantity', opt.value, true);
                  }}
                >
                  <div>
                    <p className="text-1">
                      <b>{amount}</b> de
                    </p>
                    <p className="text-2">{value}</p>
                  </div>

                  <Checkbox
                    disabled={disabled}
                    variant="radio"
                    label={null}
                    checked={selected}
                    handleChange={(v) => {
                      if (disabled) return;

                      if (v) {
                        formProposal.form.setValue('installmentQuantity', opt.value, true);
                      }
                    }}
                  />
                </div>
              );
            })}
          </div>

          {installments.hasMore && (
            <Button
              id="btn-simulate-another"
              variant="flat"
              className="btn-other-date custom-clear-button"
              onClick={() => {
                setModalInstallments(true);

                if (isMobile) {
                  scrollToTop();
                } else {
                  dynamicScrollTo('.proposal .installment-group');
                }
              }}
            >
              <img alt="" src="assets/simulador-energia-solar/jornada/ic-calendar.svg" />
              Simular outro prazo
            </Button>
          )}

          <p className="text-subtitle title-installment">Escolha a data do vencimento</p>

          <Controller
            name="firstDueDate"
            control={formProposal.form.control}
            render={(renderOptions) => {
              const field = formProposal.fields.fieldDefinition.firstDueDate;
              const fieldProps = combineFieldProps(field, renderOptions);

              const minDate = offer?.minFirstDueDate
                ? DateTime.fromFormat(offer?.minFirstDueDate, 'yyyy-MM-dd').toJSDate()
                : new Date();

              const maxDate = offer?.maxFirstDueDate
                ? DateTime.fromFormat(offer?.maxFirstDueDate, 'yyyy-MM-dd').toJSDate()
                : new Date();

              let currentValue = null;

              try {
                const c = DateTime.fromFormat(fieldProps.value, 'dd/MM/yyyy');

                if (!c.isValid) {
                  currentValue = null;
                } else {
                  currentValue = c.toJSDate();
                }
              } catch (error) {
                currentValue = null;
              }

              return (
                <div className="field-wrapper first-due-date">
                  <DatePickerInline
                    value={currentValue}
                    datePickerProps={{
                      minDate,
                      maxDate,
                    }}
                    textFieldProps={fieldProps}
                    onChange={(date) => {
                      if (!date) return;

                      const f = DateTime.fromJSDate(date);
                      if (!f.isValid) return;

                      const formatted = f.toFormat('dd/MM/yyyy');
                      formProposal.form.setValue('firstDueDate', formatted, true);
                    }}
                    onClose={() => {
                      formProposal.updateFirstDueDate(fieldProps.value);
                    }}
                  />
                </div>
              );
            }}
          />

          <hr className="divider" />

          <p className="text-subtitle-2">
            {isMobile ? 'Opções de pagamento' : 'Forma de pagamento'}
          </p>

          {offer?.showTips && (
            <div className="tips">
              <b>Dica!</b>
              <p>
                Com pagamento em débito em conta você mantém suas parcelas em dia e ganha desconto
                na parcela.
              </p>

              <img
                className="img-tip"
                alt=""
                src="assets/simulador-energia-solar/jornada/proposal-tip.svg"
              />
            </div>
          )}

          <div className="radio-group">
            {offer?.paymentOptions?.map((opt) => {
              return (
                <Checkbox
                  id={`payment-${opt.value}`}
                  key={`payment-${opt.value}`}
                  label={opt.label}
                  variant="radio"
                  checked={formProposal.currentPaymentMethod === opt.value}
                  handleChange={(v) => {
                    if (v) {
                      formProposal.form.setValue('paymentMethod', opt.value);
                      formProposal.updatePaymentMethod(opt.value);
                    }
                  }}
                />
              );
            })}
          </div>

          {formProposal.fields.paymentFields.length > 0 && (
            <>
              <p className="text-subtitle-3">Informe os dados da conta para o débito em conta.</p>

              <div className="form-render">
                {formProposal.fields.paymentFields.map((field) => {
                  return (
                    <Controller
                      key={`field-${field.name}`}
                      name={field.name}
                      control={formProposal.form.control}
                      render={(renderOptions) => {
                        const isSelect = field.type === 'select';
                        const fieldProps = combineFieldProps(field, renderOptions);

                        return (
                          <div className={clsx({ 'field-wrapper': true })}>
                            {isSelect && (
                              <Select
                                {...fieldProps}
                                handleValueChange={(v) => {
                                  formProposal.form.setValue(field.name, v, true);
                                }}
                              />
                            )}
                            {!isSelect && <TextField {...fieldProps} />}
                          </div>
                        );
                      }}
                    />
                  );
                })}
              </div>
            </>
          )}

          {!hideInsurance && <hr className="divider hide-mobile" />}

          <div
            className={clsx({
              protection: true,
              'not-available': hideInsurance,
            })}
            id="insurance"
          >
            <div className="insurance-wrapper">
              <img
                alt=""
                src="assets/simulador-energia-solar/jornada/proposal-seguro.svg"
                className="logo-seguro"
              />
              <div>
                <p className="text-title-insurance">Seguro Proteção Financeira</p>
                <p className="text-subtitle">Que tal se proteger de imprevistos?</p>
                <p className="text-subtitle-2 description">
                  Um seguro ágil e simples que te protege em casos de desemprego involuntário,
                  morte, invalidez ou incapacidade física temporária, garantindo o pagamento de
                  parte da dívida.
                </p>
              </div>
            </div>

            <div className="seguro-info">
              <img alt="" src="assets/simulador-energia-solar/jornada/ic-info.svg" />
              <p>A oferta de Seguro proteção financeira é opcional.</p>
            </div>

            <div className="insurance">
              {offer?.insuranceOptions &&
                offer.insuranceOptions.map((opt) => {
                  let title = String(opt.label);

                  if (!title.toLowerCase().includes('seguro')) {
                    title = `Seguro ${title}`;
                  }

                  return (
                    <div className="insurance-card" key={`ins-${opt.value}`} data-value={opt.value}>
                      <div className="card-header">
                        <p className="card-title">{title}</p>
                        <div className="card-status">
                          <span className={clsx('status-flag', !!selectedInsurance && 'included')}>
                            {!!selectedInsurance ? 'Incluído' : 'Não incluído'}
                          </span>
                          <Button
                            variant="flat"
                            className="btn-show-more"
                            onClick={() => {
                              setModalInsuranceDetails(opt);
                            }}
                          >
                            Mostrar detalhes
                          </Button>
                        </div>
                      </div>

                      <div className="card-item">
                        <img alt="" src="assets/simulador-energia-solar/jornada/ic-seguro-1.svg" />
                        <p className="card-text">
                          Mais tranquilidade no pagamento do seu financiamento
                        </p>
                      </div>
                      <div className="card-item">
                        <img alt="" src="assets/simulador-energia-solar/jornada/ic-seguro-2.svg" />
                        <p className="card-text">
                          Garantimos o pagamento do saldo devedor limitado a R$70.000,00.
                        </p>
                      </div>
                      <div className="card-item">
                        <img alt="" src="assets/simulador-energia-solar/jornada/ic-seguro-3.svg" />
                        <p className="card-text">Limpeza das placas solares</p>
                      </div>

                      <p className="card-text">Por apenas</p>
                      <b className="card-value">
                        <b>{opt.cost}</b> / mês
                      </b>

                      <Button
                        variant="flat"
                        className="btn-remove-insurance"
                        onClick={(ev) => {
                          ev.preventDefault();

                          if (!!selectedInsurance) {
                            setModalRemoveInsurance(true);
                            return;
                          }

                          handleAddInsurance(opt.id);
                        }}
                      >
                        {!!selectedInsurance ? 'Simular sem seguro' : 'Adicionar seguro'}
                      </Button>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* submit */}
          <div className="button-row-form">
            <Button
              variant="white"
              onClick={(ev) => {
                ev.preventDefault();
                scrollToTop();
                softRestartSimulation(true);
                setSimulationStep(
                  simulationType === 'new'
                    ? SIMULATION_STEPS.FORM_USAGE
                    : SIMULATION_STEPS.FORM_BASIC_INFO,
                );
              }}
            >
              Voltar
            </Button>
            <Button
              variant="red"
              type="submit"
              onClick={(ev) => {
                ev.preventDefault();
                formProposal.handleSubmit(ev);
              }}
            >
              Continuar
            </Button>
          </div>
        </ContainerHome24>
      </div>

      {!hidden && (
        <>
          <ModalRemoveInsurance
            onRemove={() => {
              formProposal.form.setValue('insurance', '', true);
              setModalRemoveInsurance(false);
            }}
          />
          <ModalInstallments
            offer={offer}
            onUpdateInstallmentCount={(v) => {
              formProposal.form.setValue('installmentQuantity', v, true);
            }}
          />
          <ModalVoucher />
          <ModalInsuranceDetails />
        </>
      )}

      <style jsx>{Styles}</style>
    </>
  );
}

export default FormProposal;
